import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Input } from "../../components/ui";
import { useFormValidation } from "../../hooks";
import { commonValidationRules } from "../../utils/validation";
import { Link } from "react-router-dom";
import ErrorBoundary from "../../components/ui/ErrorBoundary/ErrorBoundary";
import Alert from "../../components/ui/Alert/Alert";

interface RegistrationForm {
  name: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const Registration: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const loginHref = redirectParam
    ? `/login?redirect=${encodeURIComponent(redirectParam)}`
    : "/login";

  const [alert, setAlert] = React.useState<{ type: "success" | "error"; message: string } | null>(
    null
  );

  const handleSubmit = async (values: RegistrationForm) => {
    try {
      const response = await fetch("/api/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        setAlert({
          type: "success",
          message:
            "Registration successful! Check your email to verify your account before logging in.",
        });
        setTimeout(() => navigate(loginHref), 3000);
      } else {
        // Handle server errors
        let errorMessage = "Registration failed";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // If we can't parse JSON, it might be an HTML error page
          const textResponse = await response.text();
          if (textResponse.includes("502 Bad Gateway")) {
            errorMessage = "Server temporarily unavailable. Please try again.";
          } else if (textResponse.includes("nginx")) {
            errorMessage = "Connection error. Please check your internet connection.";
          }
        }
        setAlert({ type: "error", message: errorMessage });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: "Something went wrong. Please try again later: " + error,
      });
    }
  };

  const form = useFormValidation<RegistrationForm>({
    initialValues: {
      name: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    validationRules: {
      name: commonValidationRules.name,
      username: commonValidationRules.username,
      email: commonValidationRules.email,
      password: commonValidationRules.password,
      confirmPassword: {
        required: true,
        custom: (value: unknown, allValues?: Record<string, unknown>): string | null => {
          if (!value) return "Confirm Password is required";
          if (value !== allValues?.password) return "Passwords do not match";
          return null;
        },
      },
    },
    onSubmit: handleSubmit,
  });

  return (
    <ErrorBoundary>
      {/* Alert for error/success */}
      {alert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md">
          <Alert
            variant={alert.type === "success" ? "success" : "error"}
            dismissible
            onDismiss={() => setAlert(null)}
          >
            {alert.message}
          </Alert>
        </div>
      )}

      <div className="bg-zinc-950 text-white min-h-screen flex flex-col">
        {/* Nav */}
        <header className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-8 py-4 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-sm">
          <a href="/" className="flex items-center gap-3 select-none">
            <img src="/logo192.png" alt="RoleWeave" className="w-8 h-8 object-contain" />
            <span className="text-xl font-light tracking-widest">
              Role<span className="text-amber-400 font-normal">Weave</span>
            </span>
          </a>
          <nav className="flex items-center gap-6">
            <a
              href="/login"
              className="text-zinc-400 hover:text-white text-sm tracking-wide transition-colors duration-200"
            >
              Sign in
            </a>
          </nav>
        </header>

        {/* Form */}
        <main className="flex-1 flex items-center justify-center px-6 pt-24 pb-12">
          <div className="w-full max-w-md">
            <p className="text-amber-400/80 text-xs tracking-[0.45em] uppercase text-center mb-3">
              Begin your story
            </p>
            <h1 className="text-3xl md:text-4xl font-thin text-center text-white mb-10 tracking-wide">
              Create an account
            </h1>

            <div className="border border-zinc-800 bg-zinc-900 p-8">
              <form onSubmit={form.handleSubmit}>
                <div className="mb-4">
                  <Input
                    type="text"
                    name="username"
                    label="Username"
                    value={form.values.username}
                    onChange={form.handleChange("username")}
                    onBlur={form.handleBlur("username")}
                    error={form.errors.username}
                    fullWidth
                    variant="filled"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    type="text"
                    name="name"
                    label="Name"
                    value={form.values.name}
                    onChange={form.handleChange("name")}
                    onBlur={form.handleBlur("name")}
                    error={form.errors.name}
                    fullWidth
                    variant="filled"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    type="email"
                    name="email"
                    label="Email"
                    value={form.values.email}
                    onChange={form.handleChange("email")}
                    onBlur={form.handleBlur("email")}
                    error={form.errors.email}
                    fullWidth
                    variant="filled"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    type="password"
                    name="password"
                    label="Password"
                    value={form.values.password}
                    onChange={form.handleChange("password")}
                    onBlur={form.handleBlur("password")}
                    error={form.errors.password}
                    fullWidth
                    variant="filled"
                  />
                </div>
                <div className="mb-6">
                  <Input
                    type="password"
                    name="confirmPassword"
                    label="Confirm Password"
                    value={form.values.confirmPassword}
                    onChange={form.handleChange("confirmPassword")}
                    onBlur={form.handleBlur("confirmPassword")}
                    error={form.errors.confirmPassword}
                    fullWidth
                    variant="filled"
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  disabled={form.isSubmitting}
                >
                  {form.isSubmitting ? "Registering..." : "Start weaving"}
                </Button>
              </form>
              <div className="mt-6 text-center text-sm text-zinc-400">
                Already have an account?{" "}
                <Link
                  to={loginHref}
                  className="text-amber-400 hover:text-amber-300 transition-colors duration-200"
                >
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-10 px-8 border-t border-zinc-900 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 select-none">
            <img src="/logo192.png" alt="RoleWeave" className="w-5 h-5 object-contain opacity-70" />
            <span className="text-zinc-500 text-xs tracking-widest">
              Role<span className="text-amber-400/80">Weave</span>
            </span>
          </div>
          <p className="text-zinc-500 text-xs">© 2026 RoleWeave. All rights reserved.</p>
          <div className="flex gap-6">
            <a
              href="/login"
              className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200"
            >
              Sign in
            </a>
            <a
              href="/register"
              className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200"
            >
              Register
            </a>
          </div>
        </footer>
      </div>
    </ErrorBoundary>
  );
};

export default Registration;
